"use client";

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Search } from 'lucide-react';
import { format } from 'date-fns';

interface AIConversationsListProps {
  timeRange: '24h' | '7d' | '30d' | 'all';
}

export function AIConversationsList({ timeRange }: AIConversationsListProps) {
  const [searchTerm, setSearchTerm] = useState('');

  const { data: conversations, isLoading } = useQuery({
    queryKey: ['ai-conversations', timeRange],
    queryFn: async () => {
      let query = supabase
        .from('ai_conversation_logs')
        .select(`
          *,
          profiles!ai_conversation_logs_user_id_fkey(
            user_id,
            organization_id
          ),
          organizations(name),
          product_categories(name)
        `)
        .order('created_at', { ascending: false });
      
      // Apply time filter
      if (timeRange !== 'all') {
        const hours = timeRange === '24h' ? 24 : timeRange === '7d' ? 168 : 720;
        const since = new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
        query = query.gte('created_at', since);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      
      // Group by conversation_id
      const grouped = data.reduce((acc, log) => {
        if (!acc[log.conversation_id]) {
          acc[log.conversation_id] = {
            conversation_id: log.conversation_id,
            user_id: log.user_id,
            organization_name: log.organizations?.name,
            product_category_name: log.product_categories?.name,
            total_tokens: 0,
            total_cost: 0,
            message_count: 0,
            has_extraction: false,
            extraction_successful: false,
            started_at: log.created_at,
            last_activity: log.created_at,
          };
        }
        
        const conv = acc[log.conversation_id];
        conv.total_tokens += (log.input_tokens || 0) + (log.output_tokens || 0);
        conv.total_cost += log.estimated_cost_cents || 0;
        conv.message_count += 1;
        
        if (log.request_type === 'extraction') {
          conv.has_extraction = true;
          conv.extraction_successful = log.extraction_successful || false;
        }
        
        if (new Date(log.created_at) > new Date(conv.last_activity)) {
          conv.last_activity = log.created_at;
        }
        
        return acc;
      }, {} as Record<string, any>);
      
      return Object.values(grouped);
    },
  });

  const filteredConversations = conversations?.filter(conv => {
    const term = searchTerm.toLowerCase();
    return (
      conv.organization_name?.toLowerCase().includes(term) ||
      conv.product_category_name?.toLowerCase().includes(term) ||
      conv.conversation_id.toLowerCase().includes(term)
    );
  });

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by organization, category, or conversation ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        
        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">Loading conversations...</div>
        ) : !filteredConversations?.length ? (
          <div className="text-center py-8 text-muted-foreground">
            {searchTerm ? 'No conversations match your search' : 'No AI conversations recorded yet. Conversations will appear when users create specifications using the AI chat feature.'}
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Started</TableHead>
                  <TableHead>Organization</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Messages</TableHead>
                  <TableHead className="text-right">Tokens</TableHead>
                  <TableHead className="text-right">Cost</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredConversations.map((conv) => (
                  <TableRow key={conv.conversation_id}>
                    <TableCell className="text-sm">
                      {format(new Date(conv.started_at), 'MMM d, HH:mm')}
                    </TableCell>
                    <TableCell className="font-medium">
                      {conv.organization_name || 'Unknown'}
                    </TableCell>
                    <TableCell>{conv.product_category_name || 'N/A'}</TableCell>
                    <TableCell className="text-right">{conv.message_count}</TableCell>
                    <TableCell className="text-right">{conv.total_tokens.toLocaleString()}</TableCell>
                    <TableCell className="text-right">${(conv.total_cost / 100).toFixed(4)}</TableCell>
                    <TableCell>
                      {conv.has_extraction ? (
                        <Badge variant={conv.extraction_successful ? 'default' : 'destructive'}>
                          {conv.extraction_successful ? 'Extracted' : 'Failed'}
                        </Badge>
                      ) : (
                        <Badge variant="secondary">In Progress</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
